import { useState, useEffect, useMemo, useCallback } from 'react';
import { useLocation, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Paper,
  Select,
  MenuItem,
  InputLabel,
  FormControl,
  ListItemText,
  OutlinedInput,
} from '@mui/material';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';

import NowrapWrapper from '../components/NowrapWrapper';
import OverLayLoader from '../components/OverLayLoader';
import FunctionalTable from '../components/FunctionalTable';
import SubviewContainer from '../components/SubviewContainer';
import { EditableCell } from '../components/EditableCell';

import {
  getLanguages,
  getCodeById,
  transferWords,
  updateWords,
} from '../services';
import { createErrorNotification } from '../utils/createErrorNotification';
import useTitle from '../hooks/useTitle';
import { languageOption } from '../helpers/options';
import { CONSTANT_KEYS } from '../helpers/tableKeys';

const { CODE, NAME, SAMPLE, WORDSET, LANGUAGE_NAME } = CONSTANT_KEYS;

const KeywordsCatalogsView = () => {
  const [wordsData, setWordsData] = useState({});
  const [isPageLoading, setIsPageLoading] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const [currentLanguage, setCurrentLanguage] = useState(null);
  const [languageList, setLanguageList] = useState(null);
  const [comment, setComment] = useState('');

  const { codeId } = useParams();
  const { state, ...location } = useLocation();

  const { t } = useTranslation(['diagnoses', 'promptbar', 'common']);

  const title = t('promptbar:keywords');
  const pageTitle = t('promptbar:pageTitle', {
    title,
  });

  useTitle(pageTitle);

  useEffect(() => {
    const language = state?.language ?? languageOption[0];
    setCurrentLanguage(language);
    const fetchLanguages = async () => {
      try {
        const data = await getLanguages();
        setLanguageList(data.data);
      } catch (error) {
        console.log(error.message);
      } finally {
        setIsPageLoading(false);
      }
    };
    fetchLanguages();
  }, [state?.language]);

  const fetchData = useCallback(
    async ({ params, signal }) => {
      if (!languageList) {
        return;
      }

      try {
        setIsLoading(true);
        const filterParams = {
          [WORDSET]: params[WORDSET],
          [SAMPLE]: params[SAMPLE],
        };
        const result = await getCodeById(codeId, filterParams, signal);
        if (result) {
          const codeData = result.data;
          const wordsBlank = languageList.reduce((acc, lang) => {
            acc[lang.name] = [];
            return acc;
          }, {});

          const wordsetData = codeData.words.reduce((acc, word) => {
            if (word?.[LANGUAGE_NAME]) {
              const name =
                codeData.names.find(
                  el => el?.[LANGUAGE_NAME] === word[LANGUAGE_NAME],
                )?.name || '';

              acc[word[LANGUAGE_NAME]].push({
                ...word,
                code: codeData.code,
                name,
                uniqId: word.id,
              });
            }
            return acc;
          }, wordsBlank);

          setWordsData(wordsetData);
          setComment(result?.data?.comment || '');
          setIsLoading(false);
        }
      } catch (error) {
        createErrorNotification();
        console.log(error.message);
        setIsLoading(false);
      }
    },
    [languageList, codeId],
  );

  const handleChangeLanguage = event => {
    const { value } = event.target;
    setCurrentLanguage(value);
  };

  // редактирование строк
  const editHandler = codeId => {
    setWordsData(prev => ({
      ...prev,
      [currentLanguage]: prev[currentLanguage].map(el =>
        el.id === codeId ? { ...el, isEdit: true, prevState: el } : el,
      ),
    }));
  };

  const handleChangeCodeData = ({ index, value, columnName, isValid }) => {
    switch (columnName) {
      case CODE: {
        setWordsData(prev => ({
          ...prev,
          [currentLanguage]: prev[currentLanguage].map(el =>
            index === el.id ? { ...el, code: value, isValid } : el,
          ),
        }));
        break;
      }
      case SAMPLE: {
        setWordsData(prev => ({
          ...prev,
          [currentLanguage]: prev[currentLanguage].map(el =>
            index === el.id ? { ...el, sample: value, isValid } : el,
          ),
        }));
        break;
      }

      default:
        return;
    }
  };

  const cancelChangesHandler = codeId => {
    setWordsData(prev => ({
      ...prev,
      [currentLanguage]: prev[currentLanguage].map(el =>
        codeId === el.id ? { ...el.prevState } : el,
      ),
    }));
  };

  const applyChangesHandler = async (codeId, data) => {
    if (data?.isValid === false) {
      return;
    }

    try {
      setIsLoading(true);
      if (data?.prevState?.[SAMPLE] !== data?.[SAMPLE]) {
        const newData = { [SAMPLE]: data[SAMPLE] };
        await updateWords(codeId, newData);

        setWordsData(prev => ({
          ...prev,
          [currentLanguage]: prev[currentLanguage].map(el => {
            if (codeId === el.id) {
              return {
                ...el,
                prevState: { ...el.prevState, [SAMPLE]: el[SAMPLE] },
              };
            }
            return el;
          }),
        }));
      }

      if (data?.prevState?.code !== data.code) {
        const newData = {
          old_code: data.prevState.code,
          new_code: data.code,
          word_id: codeId,
        };
        await transferWords(newData);

        setWordsData(prev => ({
          ...prev,
          [currentLanguage]:
            data?.prevState?.code !== data.code
              ? prev[currentLanguage].filter(el => codeId !== el.id)
              : prev[currentLanguage],
        }));
      }

      //  общий сброс
      setWordsData(prev => ({
        ...prev,
        [currentLanguage]: prev[currentLanguage].map(el => {
          if (codeId === el.id) {
            const copy = { ...el };
            delete copy.prevState;
            delete copy.isEdit;
            return copy;
          }
          return el;
        }),
      }));
    } catch (error) {
      if (error?.response?.status === 422 && error?.response?.data?.detail) {
        createErrorNotification(error.response.data.detail);
      } else {
        createErrorNotification();
      }

      console.log(error.message);
    } finally {
      setIsLoading(false);
    }
  };

  const columns = useMemo(
    () => [
      {
        Header: <NowrapWrapper title={t(`table.code`)} />,
        accessor: CODE,
        id: CODE,
        disableFilters: true,
        style: { width: '15%', maxWidth: '100px' },
        Cell: ({ value, ...cell }) => {
          const isEdit = cell.row.original.isEdit;

          return (
            <Box
              sx={{
                width: '100%',
              }}
            >
              {!isEdit ? (
                value ? (
                  <span>{value}</span>
                ) : (
                  '-'
                )
              ) : (
                <EditableCell
                  value={value}
                  cell={cell}
                  onChangeData={handleChangeCodeData}
                  pathname={location.pathname}
                />
              )}
            </Box>
          );
        },
      },
      {
        Header: <NowrapWrapper title={t(`table.name_${currentLanguage}`)} />,
        accessor: NAME,
        disableFilters: true,
        style: { width: '43%' },
      },
      {
        Header: <NowrapWrapper title={t(`table.sample_${currentLanguage}`)} />,
        accessor: SAMPLE,
        style: { width: '20%' },
        Cell: ({ value, ...cell }) => {
          const isEdit = cell.row.original.isEdit;

          return (
            <Box
              sx={{
                width: '100%',
              }}
            >
              {!isEdit ? (
                value ? (
                  <span>{value}</span>
                ) : (
                  '-'
                )
              ) : (
                <EditableCell
                  value={value}
                  cell={cell}
                  onChangeData={handleChangeCodeData}
                  pathname={location.pathname}
                />
              )}
            </Box>
          );
        },
      },
      {
        Header: <NowrapWrapper title={t(`table.words_${currentLanguage}`)} />,
        accessor: WORDSET,
        style: { width: '20%' },
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [currentLanguage, location.pathname, t],
  );

  return (
    <SubviewContainer title={t(`promptbar:words_${currentLanguage}`)}>
      {isPageLoading ? (
        <OverLayLoader />
      ) : (
        <>
          {isLoading && <OverLayLoader />}
          <Box sx={{ padding: '3rem 2rem 2rem' }}>
            <FormControl size="small" variant="standard" sx={{ m: 0 }}>
              <InputLabel id="Select-1-label" shrink={false}>
                {t('common:dictionaryLanguage')}
              </InputLabel>
              <Select
                labelId="Select-1-label"
                id="Select-1"
                name="language"
                value={currentLanguage}
                onChange={handleChangeLanguage}
                input={<OutlinedInput label={t('common:dictionaryLanguage')} />}
                IconComponent={KeyboardArrowDownIcon}
                MenuProps={{ size: 'small', autoFocus: false }}
              >
                {languageOption.map(lng => (
                  <MenuItem key={lng} value={lng}>
                    <ListItemText primary={lng.toUpperCase()} />
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {comment && (
              <FormControl fullWidth variant="standard" sx={{ mt: 5 }}>
                <InputLabel id="Input-comment-label" shrink={false}>
                  {t('common:comment')}
                </InputLabel>
                <Paper className="textfield" elevation={0}>
                  {comment}
                </Paper>
              </FormControl>
            )}

            <Box sx={{ mt: '2rem' }}>
              <FunctionalTable
                data={wordsData[currentLanguage] ?? []}
                columns={columns}
                onEdit={editHandler}
                cancelChanges={cancelChangesHandler}
                applyChanges={applyChangesHandler}
                fetchData={fetchData}
              />
            </Box>
          </Box>
        </>
      )}
    </SubviewContainer>
  );
};

export default KeywordsCatalogsView;
